import {
  ChangeDetectorRef,
  Component,
  enableProdMode,
  inject,
  OnDestroy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { loadCldr } from '@syncfusion/ej2-base';
import { CaEnvService } from './shared/services/env.service';

import {
  ApplicationInsightsService,
  Languages,
  SignalRService,
} from '@seahorse/common';
import * as gregorian from '@syncfusion/ej2-cldr-data/main/nl/ca-gregorian.json';
import * as numbers from '@syncfusion/ej2-cldr-data/main/nl/numbers.json';
import * as timeZoneNames from '@syncfusion/ej2-cldr-data/main/nl/timeZoneNames.json';
import * as numberingSystems from '@syncfusion/ej2-cldr-data/supplemental/numberingSystems.json';
import { filter, mergeMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  broadcastModal,
  BroadcastMessageModel,
} from './shared/models/broadcast.model';
import { FilterMatchMode } from 'primeng/api';
import { PrimeNG } from 'primeng/config';

loadCldr(numberingSystems, gregorian, numbers, timeZoneNames);

@Component({
  selector: 'ca-root',
  templateUrl: './customer-app.component.html',
  standalone: false,
})
export class CustomerAppComponent implements OnDestroy {
  theme: string;
  private _broadcastHubUrl =
    inject(CaEnvService).apiHostV2 + '/signalr/message-broadcast';
  private _signalRService = new SignalRService(this._broadcastHubUrl);
  private _openBroadcastModal = broadcastModal();
  private _cdRef = inject(ChangeDetectorRef);
  private primengConfig = inject(PrimeNG);

  private _destroy = new Subject<void>();

  constructor(
    applicationInsights: ApplicationInsightsService,
    translate: TranslateService,
    environment: CaEnvService
  ) {
    if (!environment.enableDebug) {
      enableProdMode();
    }

    translate.addLangs(Languages.map((language) => language.value));
    translate.setDefaultLang('nl');

    this.theme = environment.theme;
    const bodies = document.getElementsByTagName('body');
    for (let i = 0; i < bodies.length; i++) {
      bodies[i].setAttribute('apptheme', this.theme);
    }

    this.listenToBroadcast();
    this.setPrimeNgConfig();
  }

  private listenToBroadcast() {
    return this._signalRService
      .createConnection()
      .pipe(
        mergeMap(() => this._signalRService.receiveMessage),
        takeUntil(this._destroy),
        filter((message) => !!message)
      )
      .subscribe((message) => {
        this.showBroadcastMessage(message.message as BroadcastMessageModel);
      });
  }

  private showBroadcastMessage(message: BroadcastMessageModel) {
    this._openBroadcastModal(message).subscribe(() => {
      this._cdRef.detectChanges();
    });
  }

  private setPrimeNgConfig() {
    this.primengConfig.filterMatchModeOptions = {
      text: [
        FilterMatchMode.EQUALS,
        FilterMatchMode.NOT_EQUALS,
        FilterMatchMode.CONTAINS,
        FilterMatchMode.STARTS_WITH,
        FilterMatchMode.ENDS_WITH,
      ],
      numeric: [
        FilterMatchMode.EQUALS,
        FilterMatchMode.NOT_EQUALS,
        FilterMatchMode.LESS_THAN,
        FilterMatchMode.GREATER_THAN,
      ],
      date: [
        FilterMatchMode.DATE_IS,
        FilterMatchMode.DATE_IS_NOT,
        FilterMatchMode.DATE_BEFORE,
        FilterMatchMode.DATE_AFTER,
      ],
    };
  }

  ngOnDestroy() {
    this._destroy.next();
    this._destroy.complete();
  }
}
